<template>
  <fn-list
    v-if="computedNodes"
    unstyled
    class="sn-items"
  >
    <li
      v-for="(node, index) in computedNodes"
      :key="node.id"
      class="sn-item"
    >
      <fn-link
        v-if="node && node.url"
        :id="'sidebar-navigation-item-' + index"
        no-arrow
        :to="node.url"
        :aria-label="$t('accessibility.general.goToUrl', { urlName: node.title })"
        :tagged="node.variant && node.variant === 'new'"
        :hide-from-crawl="hideNavLinksFromCrawl"
        :class="{
          'nuxt-link-active': node.active || (activeNode && computedNodes.length === 1),
          'nuxt-link-exact-active': node.exactActive || (activeNode && activeNode.id === node.id),
          'is-highlight': node.variant && node.variant === 'highlight',
        }"
      >
        <span
          class="link-text-inner"
          v-html="node.title"
        />
        <fn-tag
          v-if="node.variant && node.variant === 'new'"
          new-item
        >
          {{ $t('myAccount.data.newFlag') }}
        </fn-tag>
      </fn-link>

      <fn-sidebar-navigation-items
        v-if="
          displayChildNodes &&
          (node.active ||
            node.exactActive ||
            (activeNode && (computedNodes.length === 1 || activeNode.id === node.id)) ||
            hasActiveChildNode(node))
        "
        :nodes="node.nodes"
        :hide-nav-links-from-crawl="hideNavLinksFromCrawl"
      />
    </li>
  </fn-list>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    nodes?: Array<any>;
    hideNavLinksFromCrawl?: boolean;
    displayChildNodes?: boolean;
    isAccountLayout?: boolean;
  }>(),
  { nodes: () => [] },
);

const { t } = useI18n();
const route = useRoute();

// Use non-ref to prevent URLs causing a partial rerender during same-page-component page transitions
const currentUrl = unref(useGetPageUrl({ fullPath: false }));
const searchQuery = useRoute().query.q;
const isOrdersPage = route.fullPath.includes(t('url.account.orders'));

const hasActiveChildNode = (node) => {
  if (node?.nodes?.length) {
    for (const childNode of node.nodes) {
      if (childNode.url === route.path) {
        return true;
      }
    }
  }

  return false;
};

const findActiveNode = (currentUrl, nodes, routeCategory) => {
  return nodes.find((node) => {
    const categoryURL = decodeURIComponent(node.url);
    const categories = categoryURL.split(':');
    const categoryIndex = categories.lastIndexOf('category');
    const pathMatch = categories[categoryIndex + 1];

    if (props.isAccountLayout && isOrdersPage) {
      return node.url.includes(t('url.account.orders'));
    }

    // const isNodeUrl = useIsPageUrl(node.url, false);
    const isNodeUrl = currentUrl === node.url;

    if (routeCategory === pathMatch || unref(isNodeUrl)) {
      return {
        ...node,
        isActive: true,
      };
    }

    if (!props.displayChildNodes && node.nodes?.length) {
      return findActiveNode(currentUrl, node.nodes, routeCategory);
    }

    return false;
  });
};

const computedNodes = computed(() => {
  return props.nodes?.map((rawNode) => {
    const node = { ...rawNode };

    // Early return
    if (!node.url) {
      return node;
    }

    // Remove '/search' from url, when we are not on search page
    if (route.path !== '/search/') {
      node.url = node.url.replace(/\/search/gi, '');
    }

    // Add a trailing slash to prevent redirect
    if (node.url.includes('/search?')) {
      node.url = node.url.replace('/search?', '/search/?');
    }

    return node;
  });
});

const activeNode = computed(() => {
  let routeCategory = null;

  // search page navigation and highlighting
  if (searchQuery) {
    const routeParams = searchQuery.split(':');
    const lastCategory = routeParams.lastIndexOf('category');
    routeCategory = routeParams[lastCategory + 1];
  }

  const computedNodesValue = unref(computedNodes);

  if (computedNodesValue.length === 1) {
    return {
      ...computedNodesValue[0],
      isActive: true,
    };
  }

  return findActiveNode(currentUrl, computedNodesValue, routeCategory) || null;
});
</script>

<style lang="scss" scoped>
.sn-items {
  display: none;
  list-style: none;
  margin: 0;
  padding: 0;

  .link {
    display: block;
    padding: 5px 0;
    font-size: map-get($font-sizes, 'l');

    & + .sn-items {
      display: block;

      .link {
        padding-left: $space;
      }
    }

    &.nuxt-link-exact-active {
      font-weight: bold;
    }

    &.is-highlight {
      background-color: palette(orientation, yellow);
      border-radius: map-get($border-radiuses, 'xl');
      color: $color-general-text-inverse-default;
      display: inline-block;
      padding: 4px 12px;
      font-weight: map-get($font-weights, 'bold');
    }
  }
}
</style>
